import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder, UntypedFormGroup,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { filter, map, takeUntil, withLatestFrom } from 'rxjs/operators';
import { selectIsIra, selectIsPtrs, selectTemplatePrefilledFlowStarted } from '../../../account-info-header/reducers';
import { ModifiedPayeeNames } from '../../../modified-first-parties/models/FirstParties';
import { selectModifiedFirstPartyPayeesFormatted } from '../../../modified-first-parties/reducers';
import { Tooltip } from '../../../shared/models/Tooltip';
import { ApiEndpointType, Constants, WireAbaCodes } from '../../../shared/utilities/Constants';
import * as CustomValidators from '../../../shared/utilities/FormValidators';
import { mmddyyyyWithSlashes } from '../../../shared/utilities/regex-patterns';
import { TransactionFormActions } from '../../actions';
import { TaxWithholdingFields, TransactionFormServiceError, WireTransactionDetails } from '../../models/form/TransactionForm';
import { DistributionReason } from '../../models/internal-transfer/DistributionReason';
import { Country } from '../../models/shared/Country';
import { ReceivingAccountHolder, ReceivingAccountHolderIso, TaxRemittance, WireInfo } from '../../models/wire/WireInfo';
import { missingIntermediaryAddress, missingThirdPartyAddress, selectCanDisplayCreditToInfo, selectIsFedWire, selectIsFromEdit, selectIsFromEditSubmittedTransaction, selectModifiedPayeeCheckbox, selectOpenTwePopupConditions, selectPtrsInfo, selectReceivingAccountHolder, selectReceivingAccountHolderIso, selectRecipientSelection, selectTransactionFormHasError, selectTaxRemittance, selectTransactionFormPrefilledState, selectTransactionFormServiceError, selectTransactionFormTransactionDetails, selectTrustIncomePercent, selectTrustIncomeToPercent, selectTrustPrincipalPercent, selectTrustPrincipalToPercent, selectTrustTaxCategories, selectTrustTaxCodes, selectUseStandingAuth, selectUseTemplate, selectWireAbaCode, selectWireAvailableCashMMF, selectWireAvailableCashMargin, selectWireCountries, selectWireDetailsError, selectWireDetailsWarning, selectWireDistributionReason, selectWireDistributionReasonMap, selectWireHasCustodianRole, selectWireInfo, selectWireIntermediarySelection, selectWireIsoModifiedPayeeName, selectWireModifiedPayeeName1, selectWireModifiedPayeeName2, selectWirePayeeName, selectWireProcessDateFields, selectWireTaxWithholding, selectPalAgreementText, selectPalAgreementAcknowledgement } from '../../reducers';
import { currencyStringToNumber } from '../../utilities/currencyStringToNumber';
import { buildFromPrefilledForm } from '../../utilities/formUtilities';
import { TrustTaxCategory } from '../../../shared/types/TrustTaxCategory';
import { selectTrustTaxCategoriesFromInquiry, selectTrustTaxCodesFromInquiry } from '../../../transaction-view/reducers';
import { PtrsInfo } from '../../models';
import { DrupalFormService } from '../../../shared/services/DrupalForm.service';

@Component({
  standalone: false,
  selector: 'mm-transaction-details-wire-interim-iso',
  templateUrl: './transaction-details-wire-interim-iso.component.html',
  styleUrls: ['./transaction-details-wire-interim-iso.component.scss'],
})
export class TransactionDetailsWireInterimIsoComponent implements OnInit, OnDestroy {
  @Input() parentForm!: UntypedFormGroup;
  @Input() prefilledPrompts: WireTransactionDetails | undefined;
  @Input() palAccountInContext: boolean = false;
  transactionDetailsGroup: UntypedFormGroup;
  transactionFormServiceError$: Observable<TransactionFormServiceError>;
  receivingAccountHolderIso$: Observable<ReceivingAccountHolderIso>;
  receivingAccountHolder$: Observable<ReceivingAccountHolder>;
  taxRemittance$: Observable<TaxRemittance>;
  firstParty$: Observable<'firstParty' | 'thirdParty' | ''>;
  countryList$: Observable<Country[]>;
  destroy$: Subject<boolean> = new Subject()
  hasServiceError: boolean;
  isIra$: Observable<boolean>;
  isPtrs: boolean;
  validRecipient: boolean;
  taxWithHolding: TaxWithholdingFields;
  availableCashMMF: string;
  availableCashMargin: string;
  wireDetailsError$: Observable<string>;
  showIntermediaryFields$: Observable<boolean> | undefined;
  wireDetailsWarning$: Observable<string>;

  readonly controlNames = {
    selectedDate: 'selectedDate'
  };
  canDisplayCreditToInfo$: Observable<boolean>;
  defaultProcessDate: string;
  datePickerLabelName: string = 'Process date';
  datePickertooltip: Tooltip = {
    position: 'right',
    containsLink: false,
    content: Constants.wireProcessDateToolTip
  };
  datePickerValidators: ValidatorFn[] = [
    Validators.required,
    Validators.pattern(mmddyyyyWithSlashes),
    CustomValidators.notPastDate,
    CustomValidators.notGreaterThanMaxDate(this.maxDate),
    CustomValidators.notAWeekend
  ];
  distributionReason: DistributionReason;
  distributionReason$: Observable<DistributionReason>;
  distributionReasonMap$: BehaviorSubject<DistributionReason[]> = new BehaviorSubject<DistributionReason[]>([]);
  warningText: string;
  verifyCallFinished: boolean;
  wireAbaCode$: Observable<string>;
  intermediaryRequiredText: string = Constants.intermediaryRequiredMessage;
  abaCodes = WireAbaCodes;
  useSloa$: Observable<boolean>;
  useSloaAndTemplates: boolean;
  isCountryDomestic: boolean;
  useTemplate$: Observable<boolean>;
  isFedWire$: Observable<boolean>;
  serviceErrorList: string[];
  hasBadRequestServiceError: boolean;
  missingThirdPartyAddress$: Observable<boolean>;
  missingIntermediaryAddress$: Observable<boolean>;
  wireMaxTransferAmount = '$999,999,999.99';
  wireDistributionReasonTooltip = Constants.wireAchDistributionReasonTooltip;
  wireInfo$: Observable<WireInfo>;
  wireIntermediary$: Observable<'yes' | 'no' | ''>;
  firstPartyPayees$: Observable<ModifiedPayeeNames[]>;
  payeeName$: Observable<string | undefined>;
  modifiedPayeeCheckBox$: Observable<boolean | undefined>;
  modifiedPayeeName1$: Observable<string | undefined>;
  modifiedPayeeName2$: Observable<string | undefined>;
  isoModifiedPayeeName$: Observable<string | undefined>;
  fromTemplateStartTransaction: boolean = false;
  isFromEditReview: Observable<boolean>;
  wireHighDollarAmountText = Constants.wireHighDollarAmountWarningText;
  showHighDollarAmountWarning: boolean = false;
  showSufficientFundsWarning: boolean;
  futureDatedSufficientFundsWarning = Constants.futureDatedSufficientFundsWarning;
  @Input() showPaidFor: Boolean;
  taxCategories$: Observable<TrustTaxCategory[]>;
  taxCodes$: Observable<string[]>;
  trustIncomePercent$: Observable<string>;
  trustPrincipalPercent$: Observable<string>;
  ptrsInfo$: Observable<PtrsInfo>;
  @Input() displayToTrustFields: Boolean = false;
  @Input() displayFromTrustFields: Boolean = true;
  @Input() shouldStyleHorizontal: boolean = false;
  isIrsAba: boolean = false;

  trustIncomeToPercent$: Observable<string>;
  trustPrincipalToPercent$: Observable<string>;
  readonly trustReceivingAccountCopy = Constants.trustReceivingAccountCopy;
  readonly palAttestationError = Constants.moneyMovementAcknowledgeError;
  fromAccountControlNames = {
    trustIncomePercent: 'trustIncomePercent',
    trustPrincipalPercent: 'trustPrincipalPercent'
  };
  toAccountControlNames = {
    trustIncomePercent: 'trustIncomeToPercent',
    trustPrincipalPercent: 'trustPrincipalToPercent'
  };

  noPaidFor: boolean
  palAgreementText$: Observable<string>;
  palAgreementAcknowledgement$: Observable<string>;
  palAgreementItems$: Observable<string[]>;

  constructor(
    private fb: UntypedFormBuilder,
    private store: Store,
    private drupalFormService: DrupalFormService
  ) { }

  ngOnInit(): void {
    this.transactionDetailsGroup = this.fb.group({});

    this.parentForm.setControl('transactionDetails', this.transactionDetailsGroup, { emitEvent: false });
    if (this.palAccountInContext) {
      this.transactionDetailsGroup.addControl('palAttestation', this.fb.control(false, Validators.requiredTrue));
      this.palAgreementAcknowledgement$ = this.store.select(selectPalAgreementAcknowledgement);
      this.palAgreementText$ = this.store.select(selectPalAgreementText);
      this.palAgreementItems$ = this.palAgreementText$.pipe(
        map(text => text ? text.split(/\s*-\s+/).map(s => s.trim()).filter(s => s.length > 0) : [])
      );
    }
    this.transactionFormServiceError$ = this.store.select(selectTransactionFormServiceError);
    this.transactionFormServiceError$.pipe(takeUntil(this.destroy$)).subscribe((val: TransactionFormServiceError) => {
      if (!(val?.name === ApiEndpointType.wireVerifyV3)) {
        if (val?.status?.toString().startsWith('4') && val.errorList) {
          this.hasBadRequestServiceError = true;
          this.serviceErrorList = val.errorList.map(err => err.detail);
        }
        else if (val?.status?.toString().startsWith('5')) {
          this.hasServiceError = true;
        }
        else {
          this.hasServiceError = val.hasError;
          this.hasBadRequestServiceError = false;
        }
      }
    })

    if (this.displayFromTrustFields) {
      this.trustIncomePercent$ = this.store.select(selectTrustIncomePercent)
      this.trustPrincipalPercent$ = this.store.select(selectTrustPrincipalPercent)

      this.store.select(selectIsFromEditSubmittedTransaction).pipe(takeUntil(this.destroy$)).subscribe((isFromEditSubmittedTransaction) => {
        if (isFromEditSubmittedTransaction) {
          this.taxCategories$ = this.store.pipe(select(selectTrustTaxCategoriesFromInquiry));
          this.taxCodes$ = this.store.pipe(select(selectTrustTaxCodesFromInquiry));
        } else {
          this.taxCategories$ = this.store.pipe(select(selectTrustTaxCategories));
          this.taxCodes$ = this.store.pipe(select(selectTrustTaxCodes));
        }
      });
    }

    this.ptrsInfo$ = this.store.select(selectPtrsInfo)

    this.ptrsInfo$
      .pipe(takeUntil(this.destroy$))
      .subscribe((ptrsInfo) => {
        if (ptrsInfo.hasError || ptrsInfo.paidFor.hasError || (!ptrsInfo.paidFor.individuals[0]?.name && !ptrsInfo.loading)) {
          this.noPaidFor = true;
        }
      })

    if (this.displayToTrustFields) {
      this.trustIncomeToPercent$ = this.store.select(selectTrustIncomeToPercent)
      this.trustPrincipalToPercent$ = this.store.select(selectTrustPrincipalToPercent)
    }
    this.receivingAccountHolderIso$ = this.store.select(selectReceivingAccountHolderIso);
    this.receivingAccountHolder$ = this.store.select(selectReceivingAccountHolder);
    this.taxRemittance$ = this.store.select(selectTaxRemittance);
    this.firstPartyPayees$ = this.store.select(selectModifiedFirstPartyPayeesFormatted);

    this.firstParty$ = this.store.select(selectRecipientSelection);

    this.countryList$ = this.store.select(selectWireCountries);

    this.isIra$ = this.store.select(selectIsIra);
    this.store.select(selectWireTaxWithholding).pipe(takeUntil(this.destroy$)).subscribe(_taxWithholdings => this.taxWithHolding = _taxWithholdings);
    this.store.select(selectIsPtrs).pipe(takeUntil(this.destroy$)).subscribe(_isPtrs => this.isPtrs = _isPtrs);
    this.store.select(selectWireAvailableCashMMF).pipe(takeUntil(this.destroy$)).subscribe(_availableCashMMF => this.availableCashMMF = _availableCashMMF);
    this.store.select(selectWireAvailableCashMargin).pipe(takeUntil(this.destroy$)).subscribe(_availableCashMargin => this.availableCashMargin = _availableCashMargin);

    this.canDisplayCreditToInfo$ = combineLatest([
      this.store.select(selectUseStandingAuth),
      this.store.select(selectCanDisplayCreditToInfo)
    ]).pipe(
      takeUntil(this.destroy$),
      map(([_useStandingAuth, _canDisplayCreditInfo]) => _useStandingAuth && _canDisplayCreditInfo)
    )

    this.useSloaAndTemplates = this.parentForm?.value?.extraPrompts?.useStandingAuth === 'yes' || this.parentForm?.value?.extraPrompts?.useBundledStandingAuth === 'yes' || this.parentForm?.value?.extraPrompts?.useTemplates === 'yes' ? true : false;
    this.useSloa$ = this.store.select(selectUseStandingAuth);
    
    this.useTemplate$ = this.store.select(selectUseTemplate);

    this.missingThirdPartyAddress$ = this.store.select(missingThirdPartyAddress);
    this.missingIntermediaryAddress$ = this.store.select(missingIntermediaryAddress);

    // IF A SLOA IS SELECTED BUILD TRANSACTIONDETAILS FORMGROUP FROM TRANSACTION FORM IN STORE
    this.store
      .select(selectUseStandingAuth)
      .pipe(
        withLatestFrom(
          this.store.select(selectTransactionFormTransactionDetails)
        ),
        filter(([useSloa, _transactionDetails]) => useSloa),
        map(([_useSloa, transactionDetails]) => transactionDetails),
        takeUntil(this.destroy$)
      )
      .subscribe((transactionDetails) => {
        buildFromPrefilledForm(
          transactionDetails,
          this.transactionDetailsGroup
        );
      });

    // IF A TEMPLATE IS SELECTED BUILD TRANSACTIONDETAILS FORMGROUP FROM TRANSACTION FORM IN STORE
    this.store
      .select(selectTemplatePrefilledFlowStarted)
      .pipe(
        withLatestFrom(
          this.store.select(selectTransactionFormTransactionDetails),
          this.isIra$
        ),
        filter(([started, _transactionDetails, _isIra]) => started),
        takeUntil(this.destroy$)
      )
      .subscribe(([_started, transactionDetails, isIra]) => {
        buildFromPrefilledForm(
          transactionDetails,
          this.transactionDetailsGroup
        );
        this.fromTemplateStartTransaction = true;
        if (!isIra) {
          this.store.dispatch(TransactionFormActions.templatePrefilledFlowComplete());
        }
      });

    this.wireIntermediary$ = this.store.select(selectWireIntermediarySelection);
    this.showIntermediaryFields$ = this.wireIntermediary$.pipe(map((selection) => selection === 'yes'));
    this.wireAbaCode$ = this.store.select(selectWireAbaCode);
    this.wireInfo$ = this.store.select(selectWireInfo);
    this.store.select(selectWireInfo).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.isIrsAba = info.isIrsAba;
    });
    this.wireDetailsError$ = this.store.select(selectWireDetailsError);
    this.wireDetailsWarning$ = this.store.select(selectWireDetailsWarning);
    this.isFedWire$ = this.store.select(selectIsFedWire);
    this.payeeName$ = this.store.select(selectWirePayeeName);
    this.modifiedPayeeCheckBox$ = this.store.select(selectModifiedPayeeCheckbox);
    this.modifiedPayeeName1$ = this.store.select(selectWireModifiedPayeeName1);
    this.modifiedPayeeName2$ = this.store.select(selectWireModifiedPayeeName2);
    this.isoModifiedPayeeName$ = this.store.select(selectWireIsoModifiedPayeeName);
    this.isFromEditReview = this.store.select(selectTransactionFormPrefilledState).pipe(map(state => state.name === 'edit-review'));
    const receivingBankCountry = this.parentForm?.value?.transactionDetails?.receivingBankCountry?.toUpperCase();
    const isCountryDomestic = receivingBankCountry == 'US' || receivingBankCountry == 'UNITED STATES';
    if (!isCountryDomestic && this.useSloaAndTemplates) {
      this.datePickertooltip.content = Constants.wireIntlProcessDateToolTip;
    }
    // LOADING REVIEW PAGE
    combineLatest([
      this.store.select(selectOpenTwePopupConditions),
      this.store.select(selectTransactionFormHasError),
    ]).pipe(
      takeUntil(this.destroy$),
    ).subscribe(([popupConditions, formHasError]) => {
      const { verifyCallFinished, openTwePopup } = popupConditions;
      if (verifyCallFinished && !openTwePopup && !formHasError && !this.hasServiceError && !this.hasBadRequestServiceError) {
        this.store.dispatch(TransactionFormActions.loadReviewPage());
      }
    });

    // REBUILD FORM FROM EDIT FLOW
    this.store.select(selectIsFromEdit).pipe(
      takeUntil(this.destroy$),
      withLatestFrom(this.isIra$),
      filter(([isFromEdit]) => isFromEdit)
    ).subscribe(([_, isIra]) => {
      buildFromPrefilledForm(
        this.prefilledPrompts,
        this.transactionDetailsGroup
      );
      if (!isIra) {
        this.store.dispatch(TransactionFormActions.editLoadComplete());
      }
    });

    combineLatest([
      this.store.select(selectRecipientSelection),
      this.store.select(selectWireProcessDateFields),
      this.store.select(selectWireHasCustodianRole),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([party, processDateFields, hasCustodianRole]) => {
        if (party === 'firstParty' && hasCustodianRole) {
          this.defaultProcessDate = processDateFields.standardProcessDates[0];
        } else if (
          processDateFields.useExtendedProcessDates &&
          processDateFields.isExtendedProcessingWindow
        ) {
          this.defaultProcessDate = processDateFields.extendedProcessDates[0];
        } else {
          this.defaultProcessDate = processDateFields.standardProcessDates[0];
        }
      });

      this.distributionReason$ = this.store.select(selectWireDistributionReason);

      this.store.select(selectWireDistributionReasonMap).pipe(takeUntil(this.destroy$)).subscribe(reasons => {
        this.distributionReasonMap$.next(reasons);
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe;
  }

  get overdraftRulesFutureDate(): Date {
    let today = new Date(new Date().setHours(0, 0, 0, 0));
    return new Date(today.setDate(today.getDate() + 1));
  }

  get availableCash(): number {
    return currencyStringToNumber(this.availableCashMargin) + currencyStringToNumber(this.availableCashMMF);
  }

  get taxWithholdingsFormGroupDetails() {
    return this.prefilledPrompts?.taxWithholdingsFormGroup;
  }

  isRecipientValid(status: boolean) {
    this.validRecipient = status;
  }

  get minDate(): Date {
    return new Date(this.defaultProcessDate);
  }

  get maxDate(): Date {
    return new Date(this.minDate.setDate(this.minDate.getDate() + Constants.futureDatedWireMaxDays));
  }

  getDrupalPdf(name: string) {
    this.drupalFormService.createDrupalForm(name);
  }

  setShowHighDollarAmountWarning(showWiresHighDollarAmountWarning: any): void {
    this.showHighDollarAmountWarning = showWiresHighDollarAmountWarning;
  }

  setShowSufficientFundsWarning(showSufficientFundsWarning: boolean): void {
    this.showSufficientFundsWarning = showSufficientFundsWarning;
  }
}
